import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Layers, Loader2, Plus, Building2, Trash2, Power, PowerOff, ShieldCheck, Eye, UserPlus, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACCESS = {
  manager: { label: "Manager", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: ShieldCheck },
  viewer: { label: "Viewer", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30", icon: Eye },
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };

const EMPTY = { name: "", email: "", password: "", organization: "", access_level: "viewer" };

export default function SubAccounts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [orgs, setOrgs] = useState([]);

  const load = async () => {
    try { setRows((await api.get("/admin/sub-accounts")).data); }
    catch { toast.error("Failed to load sub-accounts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (open) api.get("/admin/organizations").then((r) => setOrgs(r.data)).catch(() => toast.error("Failed to load organizations"));
  }, [open]);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.organization.trim() || !form.password) {
      toast.error("Please fill in all fields"); return;
    }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      const r = await api.post("/admin/sub-accounts", form);
      setRows((x) => [r.data, ...x]);
      toast.success(`${r.data.name} added`);
      setOpen(false); setForm(EMPTY);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create sub-account"); }
    finally { setSaving(false); }
  };

  const toggle = async (u) => {
    setBusy(u.user_id);
    try {
      const r = await api.post(`/admin/sub-accounts/${u.user_id}/toggle`);
      setRows((x) => x.map((y) => (y.user_id === u.user_id ? r.data : y)));
      toast.success(`${u.name} ${r.data.status === "approved" ? "activated" : "suspended"}`);
    } catch { toast.error("Action failed"); }
    finally { setBusy(null); }
  };

  const remove = async (u) => {
    setBusy(u.user_id);
    try {
      await api.delete(`/admin/sub-accounts/${u.user_id}`);
      setRows((x) => x.filter((y) => y.user_id !== u.user_id));
      toast.success(`${u.name} removed`);
    } catch { toast.error("Delete failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1100px] mx-auto">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">Sub-Accounts</h1>
          <p className="text-gray-400 mt-1.5 text-sm">Create additional team logins under a church or organization. Assign each one a Manager or Viewer access level.</p>
        </div>
        <button data-testid="add-sub-account-btn" onClick={() => setOpen(true)} className="gold-gradient text-black font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 hover:brightness-110 transition-all active:scale-[0.98]">
          <Plus className="h-4 w-4" /> Add sub-account
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-gray-800 rounded-2xl">
          <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto"><Layers className="h-7 w-7 text-amber-400" /></div>
          <p className="mt-4 text-white font-medium">No sub-accounts yet</p>
          <p className="text-gray-500 text-sm mt-1 max-w-sm mx-auto">Add a team member login so volunteers can help run broadcasts without full admin access.</p>
          <button data-testid="empty-add-sub-account" onClick={() => setOpen(true)} className="mt-5 inline-flex items-center gap-2 gold-gradient text-black font-semibold px-4 py-2.5 rounded-lg hover:brightness-110"><UserPlus className="h-4 w-4" /> Create first sub-account</button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(rows.reduce((acc, u) => {
            const org = (u.organization || "").trim() || "No organization";
            (acc[org] = acc[org] || []).push(u);
            return acc;
          }, {})).sort((a, b) => a[0].localeCompare(b[0])).map(([org, members]) => (
            <div key={org} data-testid={`org-group-${org.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="bg-card border border-gray-800 rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 bg-gray-900/40 border-b border-gray-800">
                <Building2 className="h-4 w-4 text-amber-400" />
                <span className="text-white font-semibold text-sm">{org}</span>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">{members.length} member{members.length === 1 ? "" : "s"}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-gray-500 text-xs uppercase tracking-wide border-b border-gray-800">
                    <th className="px-5 py-3 font-medium">Member</th>
                    <th className="px-5 py-3 font-medium">Access</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Added</th>
                    <th className="px-5 py-3 font-medium text-right">Actions</th>
                  </tr></thead>
                  <tbody>
                    {members.map((u, i) => {
                      const acc = ACCESS[u.access_level] || ACCESS.viewer;
                      const AccIcon = acc.icon;
                      const active = u.status === "approved";
                      return (
                        <motion.tr key={u.user_id} data-testid={`sub-account-row-${u.user_id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.03, 0.3) }} className="border-t border-gray-800/60 hover:bg-gray-800/30">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full gold-gradient flex items-center justify-center text-xs font-bold text-black">{(u.name || "U")[0]}</div>
                              <div><p className="text-white font-medium">{u.name}</p><p className="text-xs text-gray-500">{u.email}</p></div>
                            </div>
                          </td>
                          <td className="px-5 py-3"><span className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border " + acc.cls}><AccIcon className="h-3.5 w-3.5" />{acc.label}</span></td>
                          <td className="px-5 py-3">
                            <span className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border " + (active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-gray-700/40 text-gray-400 border-gray-600/40")}>
                              <span className={"h-1.5 w-1.5 rounded-full " + (active ? "bg-emerald-400" : "bg-gray-500")} />{active ? "Active" : "Suspended"}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-gray-500 text-xs">{fmtDate(u.created_at)}</td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-2">
                              {busy === u.user_id ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : (
                                <>
                                  <button data-testid={`toggle-sub-account-${u.user_id}`} onClick={() => toggle(u)} className={"inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border " + (active ? "bg-gray-800 text-gray-300 border-gray-700 hover:text-white" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25")}>
                                    {active ? <><PowerOff className="h-3.5 w-3.5" /> Suspend</> : <><Power className="h-3.5 w-3.5" /> Activate</>}
                                  </button>
                                  <button data-testid={`delete-sub-account-${u.user_id}`} onClick={() => remove(u)} className="h-8 w-8 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                                </>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-gray-800 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-lg flex items-center gap-2"><UserPlus className="h-5 w-5 text-amber-400" /> New sub-account</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label className="text-gray-300 text-sm">Full name</Label>
              <Input data-testid="sub-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sarah Volunteer" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" /></div>
            <div className="space-y-2"><Label className="text-gray-300 text-sm">Email</Label>
              <Input data-testid="sub-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="sarah@church.org" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" /></div>
            <div className="space-y-2"><Label className="text-gray-300 text-sm">Temporary password</Label>
              <Input data-testid="sub-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" /></div>
            <div className="space-y-2"><Label className="text-gray-300 text-sm">Church / Organization</Label>
              <Select value={form.organization} onValueChange={(v) => setForm({ ...form, organization: v })}>
                <SelectTrigger data-testid="sub-organization" className="bg-gray-950 border-gray-800 h-11"><SelectValue placeholder="Choose a church or organization" /></SelectTrigger>
                <SelectContent className="bg-card border-gray-800 max-h-72">
                  {orgs.length === 0 ? <div className="px-3 py-2 text-sm text-gray-500">No organizations found</div>
                    : orgs.map((o) => <SelectItem key={o} value={o} data-testid={`sub-org-option-${o}`}>{o}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-2"><Label className="text-gray-300 text-sm">Access level</Label>
              <div className="flex gap-2">
                {["viewer", "manager"].map((lv) => {
                  const A = ACCESS[lv].icon;
                  return (
                    <button key={lv} data-testid={`sub-access-${lv}`} onClick={() => setForm({ ...form, access_level: lv })}
                      className={"flex-1 inline-flex items-center justify-center gap-2 px-3 h-11 rounded-lg text-sm border capitalize " + (form.access_level === lv ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-card text-gray-400 border-gray-800 hover:text-white")}>
                      <A className="h-4 w-4" /> {ACCESS[lv].label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-500">Managers can create/edit streams and uploads. Viewers get read-only access.</p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white border border-gray-800 flex items-center gap-1"><X className="h-4 w-4" /> Cancel</button>
              <button data-testid="save-sub-account" onClick={create} disabled={saving} className="gold-gradient text-black font-semibold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
